import React, {useState, useEffect, useContext} from "react";
import qs from 'query-string'

import * as Yup from "yup";
import {useHistory} from "react-router-dom";
import {Link as RouterLink} from "react-router-dom";
import {toast} from "react-toastify";
import {Formik, Form, Field} from "formik";

import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import BusinessOutlinedIcon from "@material-ui/icons/BusinessOutlined";
import PhoneOutlinedIcon from "@material-ui/icons/PhoneOutlined";
import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import InputAdornment from "@material-ui/core/InputAdornment";
import {makeStyles} from "@material-ui/core/styles";
import usePlans from '../../hooks/usePlans';
import {i18n} from "../../translate/i18n";
import {FormControl} from "@material-ui/core";
import {MenuItem, Select} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import {Helmet} from "react-helmet";
import {getTokens} from "../../styles/tokens";

import {openApi} from "../../services/api";
import toastError from "../../errors/toastError";
import useSettings from "../../hooks/useSettings";
import {getCurrentDomain} from "../../utils/tenant";
import ColorModeContext from "../../layout/themeContext";
import {getBackendUrl} from "../../config";
import {AuthShell, useAuthStyles} from "../Login/AuthShell";

// Opção de plano: nome e preço numa linha, limites na outra.
const useStyles = makeStyles((theme) => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    return {
        planItem: {
            display: "block",
            whiteSpace: "normal",
            paddingTop: 8,
            paddingBottom: 8,
        },
        planHead: {
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
        },
        planName: {fontSize: 14, fontWeight: 600, color: t.text},
        planPrice: {
            flex: "none",
            fontSize: 13.5,
            fontWeight: 600,
            color: t.text,
            fontVariantNumeric: "tabular-nums",
        },
        planMeta: {marginTop: 2, fontSize: 12, lineHeight: 1.45, color: t.textSecondary},
        planValue: {
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
        placeholder: {color: t.textMuted},
    };
});

const UserSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
    companyName: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
    document: Yup.string()
        .test('document-format', 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos', function (value) {
            if (!value) return false;
            const numbers = value.replace(/\D/g, '');
            return numbers.length === 11 || numbers.length === 14;
        })
        .required("CPF ou CNPJ é obrigatório"),
    password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
    email: Yup.string().email("Invalid email").required("Required"),
    phone: Yup.string().required("Required"),
});

const SignUp = () => {
    const classes = useStyles();
    const auth = useAuthStyles();
    const history = useHistory();
    const {colorMode} = useContext(ColorModeContext);
    const {appLogoFavicon, appName, mode} = colorMode;
    const {getPlanList} = usePlans()
    const [plans, setPlans] = useState([])
    const [loading, setLoading] = useState(false);
    const [validatingCnpj, setValidatingCnpj] = useState(false);
    const [lastValidatedDocument, setLastValidatedDocument] = useState("");
    const {getPublicSetting, getPublicBrand} = useSettings();

    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");
    // Termos de uso: link/versão vigentes do domínio acessado (cadeia de marca)
    const [termsUrl, setTermsUrl] = useState("");
    const [termsVersion, setTermsVersion] = useState("");

    let companyId = null
    const params = qs.parse(window.location.search)
    if (params.companyId !== undefined) {
        companyId = params.companyId
    }

    // Revenda: o backend resolve o revendedor pelo domínio acessado
    const domain = getCurrentDomain();
    const initialState = {name: "", email: "", password: "", phone: "", companyId, companyName: "", document: "", planId: "", domain, acceptTerms: false};

    const [user] = useState(initialState);

    useEffect(() => {
        const backendUrl = getBackendUrl();
        const toPublicUrl = (file) => (file ? backendUrl + "/public/" + file : "");

        const applyBrand = (brand) => {
            if (brand.userCreation === "disabled") {
                toast.error(i18n.t("signup.toasts.disabled"));
                history.push("/login");
                return;
            }
            setBackgroundLight(toPublicUrl(brand.appLogoBackgroundLight));
            setBackgroundDark(toPublicUrl(brand.appLogoBackgroundDark));
            setTermsUrl(brand.termsUrl && String(brand.termsUrl).trim() ? String(brand.termsUrl).trim() : "");
            setTermsVersion(brand.termsVersion ? String(brand.termsVersion).trim() : "");
        };

        if (companyId) {
            // compatibilidade: ?companyId= força a marca de uma empresa específica
            Promise.all([
                getPublicSetting("userCreation", companyId),
                getPublicSetting("appLogoBackgroundLight", companyId),
                getPublicSetting("appLogoBackgroundDark", companyId),
                getPublicSetting("termsUrl", companyId).catch(() => ""),
                getPublicSetting("termsVersion", companyId).catch(() => ""),
            ])
                .then(([userCreation, appLogoBackgroundLight, appLogoBackgroundDark, termsUrl, termsVersion]) =>
                    applyBrand({userCreation, appLogoBackgroundLight, appLogoBackgroundDark, termsUrl, termsVersion})
                )
                .catch((error) => {
                    console.log("Error reading setting", error);
                    setBackgroundLight("");
                    setBackgroundDark("");
                });
            return;
        }

        // Revenda: marca (e permissão de cadastro) do domínio acessado
        getPublicBrand()
            .then(applyBrand)
            .catch((error) => {
                console.log("Error reading public brand", error);
                setBackgroundLight("");
                setBackgroundDark("");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, companyId]);

    useEffect(() => {
        setLoading(true);
        const fetchData = async () => {
            // Revenda: lista planos do revendedor do domínio acessado
            const planList = await getPlanList({listPublic: "false", domain});

            setPlans(planList);
            setLoading(false);
        }
        fetchData();
    }, []);

    const validateCpf = (cpf) => {
        const cleanCpf = cpf.replace(/\D/g, '');

        if (cleanCpf.length !== 11) return false;

        if (/^(\d)\1{10}$/.test(cleanCpf)) return false;

        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cleanCpf.charAt(i)) * (10 - i);
        }
        let remainder = (sum * 10) % 11;
        if (remainder === 10 || remainder === 11) remainder = 0;
        if (remainder !== parseInt(cleanCpf.charAt(9))) return false;

        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cleanCpf.charAt(i)) * (11 - i);
        }
        remainder = (sum * 10) % 11;
        if (remainder === 10 || remainder === 11) remainder = 0;
        if (remainder !== parseInt(cleanCpf.charAt(10))) return false;

        return true;
    };

    const formatCpf = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 11);

        if (limitedNumbers.length <= 3) return limitedNumbers;
        if (limitedNumbers.length <= 6) return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3)}`;
        if (limitedNumbers.length <= 9) return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6)}`;
        return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6, 9)}-${limitedNumbers.slice(9)}`;
    };

    const formatCnpj = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 14);

        if (limitedNumbers.length <= 2) return limitedNumbers;
        if (limitedNumbers.length <= 5) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2)}`;
        if (limitedNumbers.length <= 8) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5)}`;
        if (limitedNumbers.length <= 12) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8)}`;
        return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8, 12)}-${limitedNumbers.slice(12)}`;
    };

    const detectDocumentType = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) return 'cpf';
        return 'cnpj';
    };

    const formatDocument = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) {
            return formatCpf(value);
        } else {
            return formatCnpj(value);
        }
    };

    const validateDocument = async (document, setFieldValue) => {
        const cleanDocument = document.replace(/\D/g, '');

        if (lastValidatedDocument === cleanDocument) {
            return true;
        }

        const documentType = detectDocumentType(document);

        if (documentType === 'cpf') {
            if (validateCpf(document)) {
                setLastValidatedDocument(cleanDocument);
                toast.success("CPF válido!");
                return true;
            } else {
                setLastValidatedDocument(cleanDocument);
                toast.error("CPF inválido!");
                return false;
            }
        } else {
            if (cleanDocument.length !== 14) return false;

            setValidatingCnpj(true);
            try {
                const response = await openApi.post("/auth/validate-cnpj", {cnpj: cleanDocument});
                const data = response.data;
                setValidatingCnpj(false);

                if (data.valid && data.data.nome) {
                    if (data.data.tipo === 'cnpj') {
                        setFieldValue('companyName', data.data.nome);
                        toast.success("CNPJ válido! Nome da empresa preenchido automaticamente.");
                    } else {
                        toast.success("CPF válido!");
                    }
                    setLastValidatedDocument(cleanDocument);
                    return true;
                } else {
                    setLastValidatedDocument(cleanDocument);
                    toast.error("Documento inválido ou não encontrado na Receita Federal");
                    return false;
                }
            } catch (error) {
                setValidatingCnpj(false);
                console.error("Erro ao validar documento:", error);
                toast.error("Erro ao validar documento. Tente novamente.");
                setLastValidatedDocument(cleanDocument);
                return false;
            }
        }
    };

    const handleSignUp = async values => {
        if (termsUrl && !values.acceptTerms) {
            toast.error(i18n.t("terms.mustAccept"));
            return;
        }
        try {
            const payload = {...values, acceptTerms: termsUrl ? values.acceptTerms === true : undefined};
            await openApi.post("/auth/signup", payload);
            toast.success(i18n.t("signup.toasts.success"));
            history.push("/login");
        } catch (err) {
            toastError(err);
        }
    };

    let finalBackground = "none";

    const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
    const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';

    if (mode === "light" && hasValidLightBg) {
        finalBackground = `url(${backgroundLight})`;
    } else if (mode !== "light" && hasValidDarkBg) {
        finalBackground = `url(${backgroundDark})`;
    }

    const icon = (Icon) => ({
        startAdornment: (
            <InputAdornment position="start">
                <Icon className={auth.fieldIcon}/>
            </InputAdornment>
        ),
    });

    return (
        <React.Fragment>
            <Helmet>
                <title>{appName || "Digite AI - Cadastro"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"}/>
            </Helmet>
            <CssBaseline/>

            <AuthShell
                wide
                backgroundImage={finalBackground !== "none" ? finalBackground : undefined}
            >
                {appName && <p className={auth.eyebrow}>{appName}</p>}
                <h1 className={auth.title}>{i18n.t("signup.title")}</h1>

                <Formik
                    initialValues={user}
                    enableReinitialize={true}
                    validationSchema={UserSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSignUp(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({touched, errors, isSubmitting, setFieldValue}) => (
                        <Form className={auth.form}>
                            <div className={auth.grid}>

                                <div className={auth.field}>
                                    <label htmlFor="document" className={auth.label}>CPF ou CNPJ</label>
                                    <Field
                                        as={TextField}
                                        variant="outlined"
                                        fullWidth
                                        id="document"
                                        placeholder="000.000.000-00"
                                        name="document"
                                        className={auth.textField}
                                        error={touched.document && Boolean(errors.document)}
                                        helperText={touched.document && errors.document || "Digite CPF (11 dígitos) ou CNPJ (14 dígitos)"}
                                        autoComplete="document"
                                        inputProps={{
                                            maxLength: 18,
                                            style: {textTransform: 'none'}
                                        }}
                                        InputProps={{
                                            ...icon(AssignmentIndOutlinedIcon),
                                            endAdornment: validatingCnpj && (
                                                <CircularProgress size={18}/>
                                            ),
                                        }}
                                        autoFocus
                                        onChange={(e) => {
                                            let value = e.target.value;

                                            const numbers = value.replace(/\D/g, '');

                                            if (lastValidatedDocument && lastValidatedDocument !== numbers) {
                                                setLastValidatedDocument("");
                                            }

                                            const formattedValue = formatDocument(value);
                                            setFieldValue('document', formattedValue);

                                            if (numbers.length === 11) {
                                                validateDocument(formattedValue, setFieldValue);
                                            } else if (numbers.length === 14) {
                                                validateDocument(formattedValue, setFieldValue);
                                            }
                                        }}
                                    />
                                </div>

                                <div className={auth.field}>
                                    <label htmlFor="companyName" className={auth.label}>{i18n.t("signup.form.company")}</label>
                                    <Field
                                        as={TextField}
                                        variant="outlined"
                                        fullWidth
                                        id="companyName"
                                        placeholder={i18n.t("signup.form.company")}
                                        error={touched.companyName && Boolean(errors.companyName)}
                                        helperText={touched.companyName && errors.companyName}
                                        name="companyName"
                                        autoComplete="companyName"
                                        className={auth.textField}
                                        InputProps={icon(BusinessOutlinedIcon)}
                                    />
                                </div>

                                <div className={auth.field}>
                                    <label htmlFor="name" className={auth.label}>{i18n.t("signup.form.name")}</label>
                                    <Field
                                        as={TextField}
                                        autoComplete="name"
                                        name="name"
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                        variant="outlined"
                                        fullWidth
                                        id="name"
                                        placeholder={i18n.t("signup.form.name")}
                                        className={auth.textField}
                                        InputProps={icon(PersonOutlineIcon)}
                                    />
                                </div>

                                <div className={auth.field}>
                                    <label htmlFor="email" className={auth.label}>{i18n.t("signup.form.email")}</label>
                                    <Field
                                        as={TextField}
                                        variant="outlined"
                                        fullWidth
                                        id="email"
                                        placeholder={i18n.t("signup.form.email")}
                                        name="email"
                                        error={touched.email && Boolean(errors.email)}
                                        helperText={touched.email && errors.email}
                                        autoComplete="email"
                                        inputProps={{style: {textTransform: 'lowercase'}}}
                                        className={auth.textField}
                                        InputProps={icon(MailOutlineIcon)}
                                    />
                                </div>

                                <div className={auth.field}>
                                    <label htmlFor="password" className={auth.label}>{i18n.t("signup.form.password")}</label>
                                    <Field
                                        as={TextField}
                                        variant="outlined"
                                        fullWidth
                                        name="password"
                                        error={touched.password && Boolean(errors.password)}
                                        helperText={touched.password && errors.password}
                                        placeholder="••••••••"
                                        type="password"
                                        id="password"
                                        autoComplete="current-password"
                                        className={auth.textField}
                                        InputProps={icon(LockOutlinedIcon)}
                                    />
                                </div>

                                <div className={auth.field}>
                                    <label htmlFor="phone" className={auth.label}>{i18n.t("signup.form.phone")}</label>
                                    <Field
                                        as={TextField}
                                        variant="outlined"
                                        fullWidth
                                        id="phone"
                                        placeholder={i18n.t("signup.form.phone")}
                                        name="phone"
                                        error={touched.phone && Boolean(errors.phone)}
                                        helperText={touched.phone && errors.phone}
                                        autoComplete="phone"
                                        className={auth.textField}
                                        InputProps={icon(PhoneOutlinedIcon)}
                                    />
                                </div>

                                <div className={`${auth.field} ${auth.full}`}>
                                    <label htmlFor="plan-selection" className={auth.label}>Plano</label>
                                    <FormControl variant="outlined" fullWidth className={auth.textField}>
                                        <Field
                                            as={Select}
                                            variant="outlined"
                                            fullWidth
                                            id="plan-selection"
                                            name="planId"
                                            required
                                            displayEmpty
                                            startAdornment={(
                                                <InputAdornment position="start">
                                                    <WorkOutlineIcon className={auth.fieldIcon}/>
                                                </InputAdornment>
                                            )}
                                            renderValue={(value) => {
                                                const plan = plans.find((item) => item.id === value);
                                                if (!plan) {
                                                    return <span className={classes.placeholder}>Selecione um plano</span>;
                                                }
                                                return (
                                                    <span className={classes.planValue}>
                                                        {plan.name} · R$ {plan.amount}
                                                    </span>
                                                );
                                            }}
                                        >
                                        {plans.map((plan, key) => (
                                            <MenuItem key={key} value={plan.id} className={classes.planItem}>
                                                <div className={classes.planHead}>
                                                    <span className={classes.planName}>{plan.name}</span>
                                                    <span className={classes.planPrice}>R$ {plan.amount}</span>
                                                </div>
                                                <div className={classes.planMeta}>
                                                    Atendentes: {plan.users} · WhatsApp: {plan.connections} · Filas: {plan.queues}
                                                </div>
                                            </MenuItem>
                                        ))}
                                        </Field>
                                    </FormControl>
                                </div>

                                {termsUrl && (
                                    <div className={auth.full}>
                                        <FormControlLabel
                                            className={auth.terms}
                                            control={(
                                                <Field
                                                    as={Checkbox}
                                                    type="checkbox"
                                                    name="acceptTerms"
                                                    id="acceptTerms"
                                                    color="primary"
                                                    required
                                                />
                                            )}
                                            label={(
                                                <span className={auth.termsLabel} style={{display: "block"}}>
                                                    {i18n.t("terms.acceptPrefix")}{" "}
                                                    <Link
                                                        href={termsUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={auth.link}
                                                    >
                                                        {i18n.t("terms.acceptLink")}
                                                    </Link>
                                                    {termsVersion ? ` (${i18n.t("terms.version").toLowerCase()} ${termsVersion})` : ""}
                                                </span>
                                            )}
                                        />
                                    </div>
                                )}

                            </div>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                className={auth.submit}
                                disabled={validatingCnpj}
                                endIcon={<ArrowForwardIcon/>}
                            >
                                {i18n.t("signup.buttons.submit")}
                            </Button>
                            <div className={auth.links}>
                                <Link
                                    href="#"
                                    component={RouterLink}
                                    to="/login"
                                    className={auth.link}
                                >
                                    {i18n.t("signup.buttons.login")}
                                </Link>
                            </div>
                        </Form>
                    )}
                </Formik>
            </AuthShell>
        </React.Fragment>
    );
};

export default SignUp;
